import React, { useState, useEffect } from 'react';
import formulaEngine from './utils/FormulaEngine';

const AdvancedFormulaManager = ({ 
  isOpen, 
  onClose, 
  savedComponents = [], 
  organizationalLevels = {},
  statutoryRates = {},
  onFormulaSelect,
  selectedFormula = null 
}) => {
  const [formulas, setFormulas] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredFormulas, setFilteredFormulas] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingFormula, setEditingFormula] = useState(null);
  const [formulaData, setFormulaData] = useState({
    name: '',
    description: '',
    formula: '',
    category: 'earning', // earning, deduction, benefit, reimbursement
    isActive: true,
    variables: [],
    validationErrors: []
  });
  const [validationResult, setValidationResult] = useState(null);
  const [showValidation, setShowValidation] = useState(false);

  // Available formula functions
  const formulaFunctions = [
    { name: 'SUM', description: 'Sum of values', syntax: 'SUM(value1, value2, ...)' },
    { name: 'AVG', description: 'Average of values', syntax: 'AVG(value1, value2, ...)' },
    { name: 'MAX', description: 'Maximum value', syntax: 'MAX(value1, value2, ...)' },
    { name: 'MIN', description: 'Minimum value', syntax: 'MIN(value1, value2, ...)' },
    { name: 'ROUND', description: 'Round to decimal places', syntax: 'ROUND(value, decimals)' },
    { name: 'CEIL', description: 'Round up', syntax: 'CEIL(value)' },
    { name: 'FLOOR', description: 'Round down', syntax: 'FLOOR(value)' },
    { name: 'ABS', description: 'Absolute value', syntax: 'ABS(value)' },
    { name: 'IF', description: 'Conditional logic', syntax: 'IF(condition, true_value, false_value)' },
    { name: 'AND', description: 'Logical AND', syntax: 'AND(condition1, condition2, ...)' },
    { name: 'OR', description: 'Logical OR', syntax: 'OR(condition1, condition2, ...)' },
    { name: 'NOT', description: 'Logical NOT', syntax: 'NOT(condition)' }
  ];

  // Comparison symbols
  const comparisonSymbols = [
    { symbol: '==', description: 'Equal to' },
    { symbol: '!=', description: 'Not equal to' },
    { symbol: '>', description: 'Greater than' },
    { symbol: '<', description: 'Less than' },
    { symbol: '>=', description: 'Greater than or equal' },
    { symbol: '<=', description: 'Less than or equal' }
  ];

  // Operators
  const operators = [
    { symbol: '+', description: 'Addition' },
    { symbol: '-', description: 'Subtraction' },
    { symbol: '*', description: 'Multiplication' },
    { symbol: '/', description: 'Division' },
    { symbol: '%', description: 'Modulo' },
    { symbol: '^', description: 'Exponentiation' },
    { symbol: '(', description: 'Open parenthesis' },
    { symbol: ')', description: 'Close parenthesis' }
  ];

  // CTC (Cost to Company) - base component for salary structure, always available (from Employee.jsx)
  const CTC_BASE_COMPONENT = {
    id: 'ctc',
    name: 'CTC',
    displayLabel: 'CTC (Cost to Company)'
  };

  // Available components = CTC first (base), then saved components (avoid duplicate CTC by id/name)
  const availableComponents = [
    CTC_BASE_COMPONENT,
    ...(savedComponents || []).filter(
      (c) => String(c.id || '').toLowerCase() !== 'ctc' && String(c.name || '').toUpperCase() !== 'CTC'
    )
  ];

  // Statutory rates for formula building
  const statutoryOptions = [
    { name: 'PF_RATE', description: 'Provident Fund Rate', value: '12' },
    { name: 'ESI_RATE', description: 'ESI Rate', value: '1.75' },
    { name: 'GRATUITY_RATE', description: 'Gratuity Rate', value: '4.81' },
    { name: 'TDS_THRESHOLD', description: 'TDS Threshold', value: '250000' },
    { name: 'MAX_PF_LIMIT', description: 'Maximum PF Limit', value: '15000' }
  ];

  // Load saved formulas from localStorage (in real app, this would be from API)
  useEffect(() => {
    const savedFormulas = JSON.parse(localStorage.getItem('salaryFormulas') || '[]');
    setFormulas(savedFormulas);
    setFilteredFormulas(savedFormulas);
  }, []);

  // Filter formulas based on search query
  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredFormulas(formulas);
    } else {
      const filtered = formulas.filter(formula =>
        formula.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        formula.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        formula.formula.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredFormulas(filtered);
    }
  }, [searchQuery, formulas]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormulaData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFormulaChange = (e) => {
    const formula = e.target.value;
    setFormulaData(prev => ({
      ...prev,
      formula: formula,
      variables: extractVariables(formula)
    }));
  };

  const extractVariables = (formula) => {
    // Use the formula engine to extract variables (availableComponents includes CTC base)
    return formulaEngine.extractVariables(formula, availableComponents, statutoryRates);
  };

  const validateFormula = () => {
    const { formula } = formulaData;

    // Use the formula engine for validation (pass availableComponents so CTC and saved components are valid)
    const validation = formulaEngine.validateFormula(formula, availableComponents, statutoryRates);
    const variables = extractVariables(formula);

    setValidationResult({
      isValid: validation.isValid,
      errors: validation.errors,
      variables: variables
    });

    return validation;
  };

  const handleSaveFormula = () => {
    const validation = validateFormula();
    if (!validation.isValid) {
      setFormulaData(prev => ({
        ...prev,
        validationErrors: validation.errors
      }));
      return;
    }

    const newFormula = {
      id: editingFormula ? editingFormula.id : Date.now().toString(),
      ...formulaData,
      createdAt: editingFormula ? editingFormula.createdAt : new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    let updatedFormulas;
    if (editingFormula) {
      updatedFormulas = formulas.map(f => f.id === editingFormula.id ? newFormula : f);
    } else {
      updatedFormulas = [...formulas, newFormula];
    }

    setFormulas(updatedFormulas);
    localStorage.setItem('salaryFormulas', JSON.stringify(updatedFormulas));
    
    // Reset form
    setFormulaData({
      name: '',
      description: '',
      formula: '',
      category: 'earning',
      isActive: true,
      variables: [],
      validationErrors: []
    });
    setShowCreateForm(false);
    setEditingFormula(null);
    setValidationResult(null);
  };

  const handleEditFormula = (formula) => {
    setEditingFormula(formula);
    setFormulaData({
      name: formula.name,
      description: formula.description,
      formula: formula.formula,
      category: formula.category,
      isActive: formula.isActive,
      variables: formula.variables || [],
      validationErrors: []
    });
    setShowCreateForm(true);
  };

  const handleDeleteFormula = (formulaId) => {
    if (window.confirm('Are you sure you want to delete this formula?')) {
      const updatedFormulas = formulas.filter(f => f.id !== formulaId);
      setFormulas(updatedFormulas);
      localStorage.setItem('salaryFormulas', JSON.stringify(updatedFormulas));
    }
  };

  const handleSelectFormula = (formula) => {
    if (onFormulaSelect) {
      onFormulaSelect(formula);
    }
  };

  const insertIntoFormula = (text) => {
    setFormulaData(prev => ({
      ...prev,
      formula: prev.formula + text
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-7xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Advanced Formula Manager
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Create and manage calculation formulas for salary components
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6">
          {!showCreateForm ? (
            // Formula List View
            <div className="space-y-6">
              {/* Search and Actions */}
              <div className="flex items-center justify-between">
                <div className="flex-1 max-w-md">
                  <input
                    type="text"
                    placeholder="Search formulas..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-redhalo-red/35 focus:border-redhalo-red"
                  />
                </div>
                <button
                  onClick={() => setShowCreateForm(true)}
                  className="ml-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-redhalo-red hover:opacity-95 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-redhalo-red/35"
                >
                  <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Create Formula
                </button>
              </div>

              {/* Formulas List */}
              <div className="border border-gray-200 rounded-lg bg-gray-50 max-h-96 overflow-y-auto">
                {filteredFormulas.length === 0 ? (
                  <div className="text-center py-8">
                    <div className="text-gray-400 mb-4">
                      <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <p className="text-sm text-gray-500 mb-2">No formulas found</p>
                    <p className="text-xs text-gray-400">Create your first formula to get started</p>
                  </div>
                ) : (
                  <div className="p-4 space-y-3">
                    {filteredFormulas.map((formula) => (
                      <div
                        key={formula.id}
                        className={`border rounded-lg p-4 bg-white transition-all duration-200 ${
                          selectedFormula?.id === formula.id
                            ? 'border-gray-300 bg-redhalo-mint/40 shadow-sm'
                            : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-2">
                              <h4 className="text-sm font-medium text-gray-900">
                                {formula.name}
                              </h4>
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                formula.category === 'earning' ? 'bg-green-100 text-green-800' :
                                formula.category === 'deduction' ? 'bg-red-100 text-red-800' :
                                formula.category === 'benefit' ? 'rh-accent-soft' :
                                'bg-purple-100 text-purple-800'
                              }`}>
                                {formula.category}
                              </span>
                              {!formula.isActive && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                  Inactive
                                </span>
                              )}
                            </div>
                            
                            {formula.description && (
                              <p className="text-xs text-gray-600 mb-2">{formula.description}</p>
                            )}
                            
                            <div className="bg-gray-100 rounded p-2 mb-2">
                              <code className="text-xs text-gray-800 font-mono">
                                {formula.formula}
                              </code>
                            </div>
                            
                            {formula.variables && formula.variables.length > 0 && (
                              <div className="flex flex-wrap gap-1">
                                <span className="text-xs text-gray-500">Variables:</span>
                                {formula.variables.map((variable, index) => (
                                  <span key={index} className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium rh-accent-soft">
                                    {variable}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                          
                          <div className="flex items-center space-x-2 ml-4">
                            {onFormulaSelect && (
                              <button
                                onClick={() => handleSelectFormula(formula)}
                                className="px-3 py-1 text-xs font-medium rounded-md rh-btn-primary hover:opacity-95 transition-colors"
                              >
                                Select
                              </button>
                            )}
                            <button
                              onClick={() => handleEditFormula(formula)}
                              className="px-2 py-1 text-xs font-medium rounded-md bg-gray-200 text-gray-700 hover:bg-gray-300 transition-colors"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDeleteFormula(formula.id)}
                              className="px-2 py-1 text-xs font-medium rounded-md bg-red-100 text-red-700 hover:bg-red-200 transition-colors"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : (
            // Formula Creation/Edit View
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  {editingFormula ? 'Edit Formula' : 'Create New Formula'}
                </h3>
                <button
                  onClick={() => {
                    setShowCreateForm(false);
                    setEditingFormula(null);
                    setFormulaData({
                      name: '',
                      description: '',
                      formula: '',
                      category: 'earning',
                      isActive: true,
                      variables: [],
                      validationErrors: []
                    });
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left Column - Formula Details */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Formula Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formulaData.name}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-redhalo-red/35 focus:border-redhalo-red"
                      placeholder="Enter formula name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description
                    </label>
                    <textarea
                      name="description"
                      value={formulaData.description}
                      onChange={handleInputChange}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-redhalo-red/35 focus:border-redhalo-red"
                      placeholder="Describe what this formula calculates"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Category
                    </label>
                    <select
                      name="category"
                      value={formulaData.category}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-redhalo-red/35 focus:border-redhalo-red"
                    >
                      <option value="earning">Earning</option>
                      <option value="deduction">Deduction</option>
                      <option value="benefit">Benefit</option>
                      <option value="reimbursement">Reimbursement</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Formula Expression *
                    </label>
                    <textarea
                      name="formula"
                      value={formulaData.formula}
                      onChange={handleFormulaChange}
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-redhalo-red/35 focus:border-redhalo-red font-mono text-sm"
                      placeholder="Enter formula expression (e.g., Basic_Salary * 0.4)"
                    />
                    
                    {/* Validation Errors */}
                    {formulaData.validationErrors.length > 0 && (
                      <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-md">
                        <p className="text-sm text-red-600 font-medium">Validation Errors:</p>
                        <ul className="text-xs text-red-600 mt-1 list-disc list-inside">
                          {formulaData.validationErrors.map((error, index) => (
                            <li key={index}>{error}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="isActive"
                      name="isActive"
                      checked={formulaData.isActive}
                      onChange={(e) => setFormulaData(prev => ({ ...prev, isActive: e.target.checked }))}
                      className="h-4 w-4 text-redhalo-navy focus:ring-redhalo-red/35 border-gray-300 rounded"
                    />
                    <label htmlFor="isActive" className="ml-2 text-sm text-gray-700">
                      Active
                    </label>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex space-x-3 pt-4">
                    <button
                      onClick={validateFormula}
                      className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-redhalo-red/35 focus:ring-offset-2 transition-colors"
                    >
                      Validate Formula
                    </button>
                    <button
                      onClick={handleSaveFormula}
                      className="px-4 py-2 rh-btn-primary rounded-md hover:opacity-95 focus:outline-none focus:ring-2 focus:ring-redhalo-red/35 focus:ring-offset-2 transition-colors"
                    >
                      {editingFormula ? 'Update Formula' : 'Save Formula'}
                    </button>
                  </div>
                </div>

                {/* Right Column - Formula Builder Tools */}
                <div className="space-y-4">
                  {/* Available Components */}
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Available Components</h4>
                    <div className="max-h-32 overflow-y-auto border border-gray-200 rounded-md bg-gray-50 p-2">
                      <div className="space-y-1">
                        {availableComponents.map((component) => (
                          <button
                            key={component.id}
                            onClick={() => insertIntoFormula(component.name)}
                            className="block w-full text-left px-2 py-1 text-xs text-gray-700 hover:bg-white hover:shadow-sm rounded transition-colors"
                          >
                            {component.displayLabel || component.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Statutory Rates */}
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Statutory Rates</h4>
                    <div className="max-h-32 overflow-y-auto border border-gray-200 rounded-md bg-gray-50 p-2">
                      <div className="space-y-1">
                        {statutoryOptions.map((statutory) => (
                          <button
                            key={statutory.name}
                            onClick={() => insertIntoFormula(statutory.name)}
                            className="block w-full text-left px-2 py-1 text-xs text-gray-700 hover:bg-white hover:shadow-sm rounded transition-colors"
                          >
                            {statutory.name} ({statutory.value}%)
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Functions */}
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Functions</h4>
                    <div className="max-h-32 overflow-y-auto border border-gray-200 rounded-md bg-gray-50 p-2">
                      <div className="space-y-1">
                        {formulaFunctions.map((func) => (
                          <button
                            key={func.name}
                            onClick={() => insertIntoFormula(func.syntax)}
                            className="block w-full text-left px-2 py-1 text-xs text-gray-700 hover:bg-white hover:shadow-sm rounded transition-colors"
                            title={func.description}
                          >
                            {func.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Operators */}
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Operators</h4>
                    <div className="grid grid-cols-4 gap-1">
                      {operators.map((op) => (
                        <button
                          key={op.symbol}
                          onClick={() => insertIntoFormula(op.symbol)}
                          className="px-2 py-1 text-xs text-gray-700 bg-gray-100 hover:bg-gray-200 rounded transition-colors"
                          title={op.description}
                        >
                          {op.symbol}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Comparison Symbols */}
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Comparisons</h4>
                    <div className="grid grid-cols-3 gap-1">
                      {comparisonSymbols.map((comp) => (
                        <button
                          key={comp.symbol}
                          onClick={() => insertIntoFormula(comp.symbol)}
                          className="px-2 py-1 text-xs text-gray-700 bg-gray-100 hover:bg-gray-200 rounded transition-colors"
                          title={comp.description}
                        >
                          {comp.symbol}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Validation Result */}
                  {validationResult && (
                    <div className={`p-3 rounded-md border ${
                      validationResult.isValid 
                        ? 'bg-green-50 border-green-200' 
                        : 'bg-red-50 border-red-200'
                    }`}>
                      <h4 className={`text-sm font-medium ${
                        validationResult.isValid ? 'text-green-800' : 'text-red-800'
                      }`}>
                        Validation Result
                      </h4>
                      {validationResult.isValid ? (
                        <div className="space-y-2">
                          <p className="text-xs text-green-600">Formula is valid!</p>
                          
                          {/* Test Calculation */}
                          <div className="mt-2">
                            <button
                              onClick={() => {
                                const mockData = formulaEngine.generateMockData(availableComponents, statutoryRates);
                                const calculation = formulaEngine.calculateFormula(formulaData.formula, mockData);
                                
                                if (calculation.success) {
                                  alert(`Test Calculation Result: ₹${calculation.result.toLocaleString()}`);
                                } else {
                                  alert(`Calculation Error: ${calculation.error}`);
                                }
                              }}
                              className="text-xs px-2 py-1 bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
                            >
                              Test Calculation
                            </button>
                          </div>
                        </div>
                      ) : (
                        <ul className="text-xs text-red-600 mt-1 list-disc list-inside">
                          {validationResult.errors.map((error, index) => (
                            <li key={index}>{error}</li>
                          ))}
                        </ul>
                      )}
                      {validationResult.variables.length > 0 && (
                        <div className="mt-2">
                          <p className="text-xs text-gray-600">Variables used:</p>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {validationResult.variables.map((variable, index) => (
                              <span key={index} className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium rh-accent-soft">
                                {variable}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdvancedFormulaManager;
